import { Injectable } from '@angular/core';
import {
    ActivatedRouteSnapshot,
    Router,
    RouterStateSnapshot,
} from '@angular/router';
import { Feature, FeatureState, SiteFeature } from '@myrtls/api-interfaces';
import { select, Store } from '@ngrx/store';
import { Observable } from 'rxjs';
import { map, skipWhile } from 'rxjs/operators';
import { AppState } from '../../store';
import { $siteFeatures } from '../../store/sites/sites.selectors';
import { RoutePath } from '../models/route-path.enum';

@Injectable({ providedIn: 'root' })
export class FeaturesGuard {
    constructor(
        private readonly router: Router,
        private readonly store: Store<AppState>,
    ) {}

    private FALLBACK_PRIORITY: SiteFeature[] = [
        SiteFeature.INSIGHTS_DASHBOARDS,
        SiteFeature.DEVICE_CARE_DASHBOARD,
        SiteFeature.DEVICE_CARE_INSTALLATION,
        SiteFeature.SITE_OVERVIEW,
    ];

    canActivate(
        next: ActivatedRouteSnapshot,
        state: RouterStateSnapshot,
    ): boolean | Observable<boolean> {
        return this.store.pipe(
            select($siteFeatures),
            skipWhile(features => {
                return features.length === 0;
            }),
            map(features => {
                const id = state.root.firstChild?.params.id;

                if (!features || typeof id !== 'string') {
                    console.log('Navigating to Companies...');
                    this.router.navigate([RoutePath.COMPANIES]);
                    return false;
                }

                if (
                    next.data.requiredFeatures === undefined ||
                    next.data.requiredFeatures.length === 0
                ) {
                    return true;
                }

                for (const requiredFeature of next.data
                    .requiredFeatures as SiteFeature[]) {
                    const feature = features.find(
                        foundFeature => foundFeature.name === requiredFeature,
                    );

                    if (!feature) {
                        return this.fallback(id, features);
                    }

                    if (feature.state !== FeatureState.ENABLED) {
                        return this.fallback(id, features);
                    }
                }

                return true;
            }),
        );
    }

    private fallback(id: string, features: Feature[]): boolean {
        for (const candidate of this.FALLBACK_PRIORITY) {
            const feature = features.find(
                f =>
                    f.name === candidate &&
                    f.state === FeatureState.ENABLED &&
                    !f.hidden,
            );
            if (feature) {
                console.log(
                    `(fallback) Navigating to ${candidate} for site ${id}...`,
                );
                this.router.navigate([RoutePath.SITE, id, candidate]);
                return false;
            }
        }

        console.log(`(fallback) Navigating to Site Overview for site ${id}...`);
        this.router.navigate([RoutePath.SITE, id, SiteFeature.SITE_OVERVIEW]);
        return false;
    }
}
